import { Loader2, ScanEye, Sparkles, Eraser, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { CULL } from '@/constants/testIds';

export const CullRunPanel = ({ run, estimate, aiEyes, onAiEyesChange, onRun, onClear, busy, imageCount }) => {
  const running = run?.status === 'running';
  const p = run?.progress || {};
  const localPct = p.total ? Math.round((p.done / p.total) * 100) : 0;
  const aiPct = p.ai_total ? Math.round((p.ai_done / p.ai_total) * 100) : 0;
  const pct = running ? (run.stage === 'ai' ? 50 + aiPct / 2 : (run.config?.ai_eyes ? localPct / 2 : localPct)) : 100;
  const stageLabel = run?.stage === 'ai' ? `Checking eyes & expressions · batch ${p.ai_done}/${p.ai_total}` : run?.stage === 'queued' ? 'Starting…' : `Measuring sharpness & duplicates · ${p.done}/${p.total}`;

  return (
    <section data-testid={CULL.runPanel} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-xl">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]"><ScanEye className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> Culling pass</p>
          <p className="mt-2 text-sm text-[hsl(var(--lxra-fg))]">Sharpness and near-duplicate detection run locally for free. Turn on the AI eye check to also flag blinks and awkward expressions — photos are sent 4 per call to keep credits low.</p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex items-center gap-2.5">
            <Label htmlFor="cull-ai" className="flex items-center gap-1.5 text-xs text-[hsl(var(--lxra-fg))]"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> AI eye & expression check</Label>
            <Switch id="cull-ai" data-testid={CULL.aiSwitch} checked={aiEyes} onCheckedChange={onAiEyesChange} disabled={running || !estimate?.ai_configured} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
          </div>
          <p data-testid={CULL.estimate} className="text-[11px] text-[hsl(var(--lxra-muted))]">
            {imageCount} photo{imageCount === 1 ? '' : 's'} · {aiEyes && estimate?.ai_configured ? `~${estimate?.ai_calls ?? Math.ceil(imageCount / 4)} AI call${(estimate?.ai_calls ?? 1) === 1 ? '' : 's'} (${estimate?.model || 'vision model'})` : 'no AI credits used'}
          </p>
          <div className="flex items-center gap-2">
            {run && !running && (
              <Button variant="ghost" size="sm" data-testid={CULL.clearButton} onClick={onClear} disabled={busy} className="h-9 text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:bg-[#1a1a1c] hover:text-[hsl(var(--lxra-fg))]"><Eraser className="mr-1.5 h-3.5 w-3.5" /> Clear results</Button>
            )}
            <Button data-testid={CULL.runButton} onClick={onRun} disabled={busy || running || imageCount === 0} className="h-9 bg-[hsl(var(--lxra-gold))] px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))]">
              {running ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ScanEye className="mr-1.5 h-3.5 w-3.5" />}
              {running ? 'Culling…' : run ? 'Run again' : 'Run culling'}
            </Button>
          </div>
        </div>
      </div>

      {running && (
        <div data-testid={CULL.progress} className="mt-4 space-y-1.5">
          <Progress value={pct} className="h-1.5 bg-[#1a1a1c] [&>div]:bg-[hsl(var(--lxra-gold))]" />
          <p data-testid={CULL.progressLabel} className="text-[11px] text-[hsl(var(--lxra-muted))]">{stageLabel}</p>
        </div>
      )}
      {run?.status === 'error' && (
        <p data-testid={CULL.error} className="mt-4 flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-950/30 px-3 py-2 text-xs text-red-200"><AlertTriangle className="h-3.5 w-3.5" /> {run.error || 'The culling pass failed.'}</p>
      )}
      {run?.ai_error && run.status === 'done' && (
        <p data-testid={CULL.aiError} className="mt-4 flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-950/20 px-3 py-2 text-xs text-amber-200"><AlertTriangle className="h-3.5 w-3.5" /> {run.ai_error}</p>
      )}
    </section>
  );
};
